<template>
  <div class="audience-page">
    <section class="audience-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Analytics</span>
          <h4>Audience</h4>
        </div>
        <div class="audience-head-actions" v-if="activeTab === 'sessions'">
          <span class="live-connection" :class="{ online: liveConnected }">
            <i></i>{{ liveConnected ? 'Live' : 'Reconnecting...' }}
          </span>
          <button class="btn-refresh" :disabled="loading" @click="refreshAudience()">
            {{ loading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
      <p class="intro" v-if="activeTab === 'sessions'">
        Visitor sessions and engagement captured first-party. Capture honors Do-Not-Track / GPC
        and consent, so totals reflect only sessions that opted in. Pick a session to inspect its
        device, source and event timeline.
      </p>
      <p class="intro" v-else>
        How the catalog performs in Google Search: ranking distribution, device gaps, AI
        visibility, index coverage, and the part pages closest to earning more clicks. Standard
        metrics are mirrored from Search Console, which finalizes data on a 2–3 day delay.
      </p>
    </section>

    <div class="tabs-row audience-tabs-row">
      <div class="tabs">
        <button class="tab" :class="{ active: activeTab === 'sessions' }" @click="activeTab = 'sessions'">
          Sessions
        </button>
        <button class="tab" :class="{ active: activeTab === 'search' }" @click="activeTab = 'search'">
          Google Search
        </button>
      </div>
    </div>

    <SearchConsole v-if="activeTab === 'search'" />

    <template v-if="activeTab === 'sessions'">
    <div v-if="error" class="alert">{{ error }}</div>

    <!-- Summary cards -->
    <div class="kpi-row">
      <div v-for="card in summaryCards" :key="card.key" class="kpi-card">
        <span class="kpi-label">{{ card.label }}</span>
        <span class="kpi-value">{{ card.value }}</span>
        <span v-if="card.note" class="kpi-note">{{ card.note }}</span>
        <span v-if="card.progress !== null" class="kpi-progress"><i :style="{ width: card.progress + '%' }"></i></span>
      </div>
    </div>

    <section class="traffic-overview" v-if="overviewSessionCount">
      <div class="overview-heading">
        <div>
          <span class="eyebrow">Traffic picture</span>
          <h5>Where human attention is coming from</h5>
        </div>
        <span class="overview-window">Last {{ summaryWindowLabel }} &middot; bots excluded</span>
      </div>

      <div class="traffic-overview-grid">
        <article class="insight-card source-mix-card">
          <div class="insight-head">
            <span>Session share</span>
            <strong>{{ topChartLabel(sourceChart) }}</strong>
          </div>
          <div class="source-mix">
            <div class="source-donut" :style="sourceDonutStyle" role="img" :aria-label="sourceDonutLabel">
              <div>
                <strong>{{ overviewSessionCount }}</strong>
                <span>sessions</span>
              </div>
            </div>
            <div class="source-legend">
              <div v-for="item in sourceChart" :key="item.label" class="source-legend-row">
                <i :style="{ backgroundColor: item.color }"></i>
                <span :title="item.label">{{ item.label }}</span>
                <strong>{{ item.count }}</strong>
                <small>{{ item.percent }}%</small>
              </div>
            </div>
          </div>
        </article>

        <article class="insight-card quality-card">
          <div class="insight-head">
            <span>Visit quality</span>
            <strong>{{ engagementRate }}% engaged</strong>
          </div>
          <div class="quality-stack" role="img" :aria-label="qualityChartLabel">
            <i
              v-for="item in nonEmptyQualityChart"
              :key="item.key"
              :style="{ width: item.percent + '%', backgroundColor: item.color }"
              :title="item.label + ': ' + item.count"
            ></i>
          </div>
          <div class="quality-list">
            <div v-for="item in qualityChart" :key="item.key" class="quality-row">
              <i :style="{ backgroundColor: item.color }"></i>
              <span>
                <strong>{{ item.label }}</strong>
                <small>{{ item.note }}</small>
              </span>
              <b>{{ item.count }}</b>
            </div>
          </div>
        </article>

        <article class="insight-card source-performance-card">
          <div class="insight-head">
            <span>Acquisition quality</span>
            <strong>Attention, not just arrivals</strong>
          </div>
          <div class="performance-head">
            <span>Source</span><span>Sessions</span><span>Engaged</span><span>Pages</span><span>Time</span>
          </div>
          <div class="performance-list">
            <div v-for="item in sourcePerformance" :key="item.label" class="performance-row">
              <span class="performance-source" :title="item.label">
                <i :style="{ width: item.engagementRate + '%' }"></i>
                <strong>{{ item.label }}</strong>
              </span>
              <span>{{ item.sessions }}</span>
              <span class="performance-engaged">{{ item.engaged }} <small>{{ item.engagementRate }}%</small></span>
              <span>{{ item.pages }}</span>
              <span>{{ fmtDuration(item.duration) }}</span>
            </div>
          </div>
          <span class="performance-note">Engaged = 2+ pages or at least 30 seconds.</span>
        </article>
      </div>
    </section>

    <!-- Backend-classified crawler activity stays separate from human KPIs. -->
    <section v-if="crawlerActivityViews.length" class="crawler-section" :class="{ expanded: crawlerExpanded }">
      <button
        class="crawler-summary-toggle"
        type="button"
        :aria-expanded="crawlerExpanded ? 'true' : 'false'"
        @click="crawlerExpanded = !crawlerExpanded"
      >
        <div>
          <span class="eyebrow">Crawler traffic</span>
          <h5>Verified and suspected crawler activity</h5>
        </div>
        <div class="crawler-summary-metrics">
          <span><strong>{{ crawlerSessions(primaryCrawlerActivity) }}</strong> sessions</span>
          <span><strong>{{ crawlerVerifiedCount(primaryCrawlerActivity) }}</strong> verified</span>
          <span><strong>{{ crawlerSuspectedCount(primaryCrawlerActivity) }}</strong> suspected</span>
          <span><strong>{{ crawlerEvents(primaryCrawlerActivity) }}</strong> events</span>
          <span><strong>{{ crawlerTrafficShare }}%</strong> of classified traffic</span>
          <span class="crawler-summary-last">
            Last seen <strong>{{ fmtRelative(crawlerLastSeen(primaryCrawlerActivity)) }}</strong>
          </span>
        </div>
        <span class="crawler-expand-label">
          {{ crawlerExpanded ? 'Hide details' : 'Show details' }}
          <span class="crawler-chevron" aria-hidden="true"></span>
        </span>
      </button>

      <div v-show="crawlerExpanded" class="crawler-details">
        <span class="crawler-section-note">Backend classified — never inferred from device, duration, or referrer.</span>
        <div class="crawler-card-grid">
        <article v-for="view in crawlerActivityViews" :key="view.key" class="crawler-card">
          <div class="crawler-card-head">
            <div>
              <span>{{ view.label }}</span>
              <strong>{{ crawlerSessions(view.activity) }} sessions</strong>
            </div>
            <span v-if="view.live" class="live-pill">Live</span>
          </div>

          <div class="crawler-metrics">
            <div>
              <span>Verified</span>
              <strong>{{ crawlerVerifiedCount(view.activity) }}</strong>
            </div>
            <div>
              <span>Suspected</span>
              <strong>{{ crawlerSuspectedCount(view.activity) }}</strong>
            </div>
            <div>
              <span>Events</span>
              <strong>{{ crawlerEvents(view.activity) }}</strong>
            </div>
            <div>
              <span>Last seen</span>
              <strong :title="fmtTime(crawlerLastSeen(view.activity))">{{ fmtRelative(crawlerLastSeen(view.activity)) }}</strong>
            </div>
          </div>

          <div class="crawler-rankings">
            <div>
              <span class="crawler-list-title">Top crawlers</span>
              <div v-if="crawlerTopCrawlers(view.activity).length" class="crawler-list">
                <div
                  v-for="(crawler, index) in crawlerTopCrawlers(view.activity)"
                  :key="crawlerItemKey(crawler, index)"
                  class="crawler-list-row"
                >
                  <strong :title="crawlerActivityLabel(crawler)">{{ crawlerActivityLabel(crawler) }}</strong>
                  <span>{{ crawlerItemCounts(crawler) }}</span>
                </div>
              </div>
              <span v-else class="crawler-list-empty">No crawler identities reported.</span>
            </div>
            <div>
              <span class="crawler-list-title">Top pages</span>
              <div v-if="crawlerTopPages(view.activity).length" class="crawler-list">
                <div
                  v-for="(page, index) in crawlerTopPages(view.activity)"
                  :key="crawlerPageKey(page, index)"
                  class="crawler-list-row"
                >
                  <strong :title="crawlerPageLabel(page)">{{ crawlerPageLabel(page) }}</strong>
                  <span>{{ crawlerPageCounts(page) }}</span>
                </div>
              </div>
              <span v-else class="crawler-list-empty">No crawler pages reported.</span>
            </div>
          </div>
        </article>
        </div>
      </div>
    </section>

    <div class="session-list-heading">
      <div>
        <span class="eyebrow">Session details</span>
        <h5>{{ sessionListTitle }}</h5>
      </div>
      <span>Last {{ summaryWindowLabel }}</span>
    </div>

    <!-- Filters -->
    <div class="filters">
      <input
        class="hfilter grow"
        type="text"
        v-model="filters.q"
        placeholder="Search visitor, IP, timezone, path, referrer, UTM, country"
        @input="onSearchInput"
        @keyup.enter="applyFilters"
      />
      <select class="hfilter" v-model="filters.device" @change="applyFilters">
        <option value="all">Any device</option>
        <option value="desktop">Desktop</option>
        <option value="mobile">Mobile</option>
        <option value="tablet">Tablet</option>
      </select>
      <select class="hfilter" v-model="filters.authenticated" @change="applyFilters">
        <option value="all">All visitors</option>
        <option value="yes">Logged in</option>
        <option value="no">Anonymous</option>
      </select>
      <select class="hfilter" v-model="filters.traffic" @change="applyFilters">
        <option value="all">All traffic</option>
        <option value="human">Humans</option>
        <option value="crawler">Crawlers</option>
        <option value="verified_bot">Verified crawlers</option>
        <option value="suspected_bot">Suspected crawlers</option>
      </select>
      <a v-if="hasActiveFilters" class="focus-clear" @click="clearFilters">clear</a>
    </div>

    <!-- Visitor groups -->
    <section class="audience-groups" :class="{ 'is-loading': loading && audienceGroups.length }">
      <div class="group-head">
        <span>Visitor / user</span>
        <span>Sessions</span>
        <span>Last source</span>
        <span>Last device</span>
        <span>Total pages</span>
        <span>Combined duration</span>
        <span>Last seen</span>
        <span></span>
      </div>

      <div v-if="!audienceGroups.length" class="empty audience-empty">
        <span v-if="loading">Loading sessions...</span>
        <span v-else>No sessions match these filters.</span>
      </div>

      <article v-for="group in audienceGroups" :key="group.key" class="visitor-group" :class="{ expanded: isGroupExpanded(group.key), 'has-amazon': group.amazonClicks }">
        <div
          class="group-row"
          role="button"
          tabindex="0"
          @click="toggleAudienceGroup(group.key)"
          @keyup.enter="toggleAudienceGroup(group.key)"
        >
          <div class="person-cell">
            <span v-if="group.isUser" class="user-name" :title="group.label">{{ group.label }}</span>
            <span v-else class="anon-pill">Anonymous</span>
            <span
              v-if="crawlerBadge(group.lastSession)"
              class="crawler-badge"
              :class="'crawler-badge--' + trafficType(group.lastSession)"
            >{{ crawlerBadge(group.lastSession) }}</span>
            <span class="muted-sub" :title="group.identity">{{ group.identity }}</span>
            <span class="network-line mono" :title="group.network">{{ group.network }}</span>
          </div>
          <div>
            <span class="sess-line">
              <span class="count-num">{{ group.sessionCount }}</span>
              <span v-if="group.live" class="live-pill">Live</span>
              <span v-if="group.amazonClicks" class="amazon-badge" :title="group.amazonClicks + ' Amazon click' + (group.amazonClicks > 1 ? 's' : '')">
                <span class="icon icon-shopping-cart"></span> {{ group.amazonClicks }}
              </span>
            </span>
            <span class="muted-sub">last {{ fmtRelative(lastSeenAt(group.lastSession)) }}</span>
          </div>
          <div>
            <span class="text-cell" :title="sourceLabel(group.lastSession)">{{ sourceLabel(group.lastSession) }}</span>
          </div>
          <div>
            <span class="device-pill" :class="'dv-' + deviceType(group.lastSession)">{{ deviceTypeLabel(group.lastSession) }}</span>
            <span class="muted-sub" :title="browserOs(group.lastSession)">{{ browserOs(group.lastSession) || '-' }}</span>
          </div>
          <div>
            <span class="count-num">{{ group.totalPages }}</span>
            <span class="muted-sub">{{ group.totalEvents }} events</span>
            <span class="muted-sub" :title="group.lastEventTitle">{{ group.lastEventLabel }}</span>
          </div>
          <div>
            <span class="when-primary">{{ fmtDuration(group.totalDuration) }}</span>
          </div>
          <div>
            <span class="when-primary" :title="fmtTime(lastSeenAt(group.lastSession))">{{ fmtRelative(lastSeenAt(group.lastSession)) }}</span>
            <span class="muted-sub">{{ fmtDateTimeShort(lastSeenAt(group.lastSession)) }}</span>
          </div>
          <span class="group-toggle">{{ isGroupExpanded(group.key) ? 'Hide' : 'Show' }}</span>
        </div>

        <div v-if="isGroupExpanded(group.key)" class="session-dropdown">
          <button
            v-for="(session, sessionIndex) in group.sessions"
            :key="sessionId(session) || sessionIndex"
            class="session-mini-row"
            type="button"
            @click.stop="openDetail(session)"
          >
            <span class="mono session-id" :title="sessionId(session)">
              <span>Session {{ shortId(sessionId(session)) }}</span>
              <span
                v-if="crawlerBadge(session)"
                class="crawler-badge"
                :class="'crawler-badge--' + trafficType(session)"
              >{{ crawlerBadge(session) }}</span>
            </span>
            <span>
              <strong>{{ fmtRelative(lastSeenAt(session)) }}</strong>
              <small>{{ fmtDateTimeShort(lastSeenAt(session)) }}</small>
            </span>
            <span>
              <strong>{{ sourceLabel(session) }}</strong>
              <small>{{ ipLabel(session) }} / {{ timezoneLabel(session) }}</small>
            </span>
            <span>
              <strong>{{ pageViews(session) }} pages</strong>
              <small>{{ eventCount(session) }} events</small>
              <span v-if="amazonClicks(session)" class="amazon-badge">
                <span class="icon icon-shopping-cart"></span> {{ amazonClicks(session) }}
              </span>
            </span>
            <span>
              <strong>{{ fmtDuration(durationSeconds(session)) }}</strong>
              <small>{{ browserOs(session) || deviceTypeLabel(session) }}</small>
            </span>
          </button>
        </div>
      </article>
    </section>

    <!-- Pagination -->
    <div class="pager" v-if="total > 0">
      <span class="pager-info">
        Showing {{ audienceGroups.length }} visitor groups from sessions {{ showingFrom }}-{{ showingTo }} of {{ total }} in the last {{ summaryWindowLabel }}
      </span>
      <div class="pager-buttons">
        <button class="row-btn" :disabled="!hasPrev || loading" @click="prevPage">Previous</button>
        <button class="row-btn" :disabled="!hasNext || loading" @click="nextPage">Next</button>
      </div>
    </div>

    <!-- Detail drawer -->
    <div v-if="selectedSession" class="audience-drawer" @click.self="closeDetail">
      <aside class="audience-panel">
        <div class="panel-head">
          <div>
            <span class="device-pill" :class="'dv-' + deviceType(selectedSession)">{{ deviceTypeLabel(selectedSession) }}</span>
            <span
              v-if="crawlerBadge(selectedSession)"
              class="crawler-badge"
              :class="'crawler-badge--' + trafficType(selectedSession)"
            >{{ crawlerBadge(selectedSession) }}</span>
            <h5>{{ userLabel(selectedSession) || 'Anonymous visitor' }}</h5>
            <span class="muted-sub mono">Session {{ shortId(sessionId(selectedSession)) }}</span>
          </div>
          <button class="panel-close" @click="closeDetail">x</button>
        </div>

        <div v-if="selectedLoading" class="panel-loading">Loading session…</div>
        <div v-if="selectedError" class="panel-error">{{ selectedError }}</div>

        <div class="metrics">
          <div>
            <span class="metric-label">Duration</span>
            <strong>{{ fmtDuration(durationSeconds(selectedSession)) }}</strong>
          </div>
          <div>
            <span class="metric-label">Page views</span>
            <strong>{{ pageViews(selectedSession) }}</strong>
          </div>
          <div>
            <span class="metric-label">Events</span>
            <strong>{{ selectedEvents.length || eventCount(selectedSession) }}</strong>
          </div>
        </div>

        <div v-if="isCrawlerSession(selectedSession)" class="panel-section">
          <span class="section-kicker">Crawler classification</span>
          <div class="spec-grid">
            <div class="spec-row"><span>Traffic type</span><strong>{{ prettyLabel(trafficType(selectedSession)) }}</strong></div>
            <div class="spec-row"><span>Provider</span><strong>{{ botProvider(selectedSession) || '—' }}</strong></div>
            <div class="spec-row"><span>Bot</span><strong>{{ botName(selectedSession) || '—' }}</strong></div>
            <div class="spec-row"><span>Verified</span><strong>{{ botVerifiedLabel(selectedSession) }}</strong></div>
            <div class="spec-row"><span>Method</span><strong>{{ botVerificationMethod(selectedSession) || '—' }}</strong></div>
          </div>
        </div>

        <div class="panel-section">
          <span class="section-kicker">Visitor</span>
          <div class="spec-grid">
            <div class="spec-row"><span>Visitor ID</span><strong class="mono">{{ shortId(visitorId(selectedSession)) }}</strong></div>
            <div class="spec-row"><span>User</span><strong>{{ userLabel(selectedSession) || 'Anonymous' }}</strong></div>
            <div class="spec-row"><span>IP</span><strong>{{ ipLabel(selectedSession) }}</strong></div>
            <div class="spec-row"><span>Location</span><strong>{{ locationLabel(selectedSession) }}</strong></div>
            <div class="spec-row"><span>Language</span><strong>{{ pick(selectedSession, ['language', 'locale'], '—') }}</strong></div>
            <div class="spec-row"><span>Timezone</span><strong>{{ timezoneLabel(selectedSession) }}</strong></div>
          </div>
        </div>

        <div class="panel-section">
          <span class="section-kicker">Device</span>
          <div class="spec-grid">
            <div class="spec-row"><span>Type</span><strong>{{ deviceTypeLabel(selectedSession) }}</strong></div>
            <div class="spec-row"><span>Browser</span><strong>{{ pick(selectedSession, ['browser'], '—') }}</strong></div>
            <div class="spec-row"><span>OS</span><strong>{{ pick(selectedSession, ['os', 'operating_system'], '—') }}</strong></div>
            <div class="spec-row"><span>User agent</span><strong class="ua">{{ pick(selectedSession, ['user_agent', 'userAgent'], '—') }}</strong></div>
          </div>
        </div>

        <div class="panel-section">
          <span class="section-kicker">Acquisition</span>
          <div class="spec-grid">
            <div class="spec-row"><span>Referrer</span><strong class="ua">{{ pick(selectedSession, ['referrer'], '—') }}</strong></div>
            <div class="spec-row"><span>UTM source</span><strong>{{ pick(selectedSession, ['utm_source', 'utmSource'], '—') }}</strong></div>
            <div class="spec-row"><span>UTM medium</span><strong>{{ pick(selectedSession, ['utm_medium', 'utmMedium'], '—') }}</strong></div>
            <div class="spec-row"><span>UTM campaign</span><strong>{{ pick(selectedSession, ['utm_campaign', 'utmCampaign'], '—') }}</strong></div>
            <div class="spec-row"><span>Click ID</span><strong>{{ clickIdLabel(selectedSession) }}</strong></div>
            <div class="spec-row"><span>Landing</span><strong class="ua">{{ pick(selectedSession, ['landing_path', 'entry_path', 'path'], '—') }}</strong></div>
          </div>
        </div>

        <div class="panel-section">
          <span class="section-kicker">Privacy</span>
          <div class="spec-grid">
            <div class="spec-row"><span>Consent (analytics)</span><strong>{{ consentLabel(selectedSession, 'analytics') }}</strong></div>
            <div class="spec-row"><span>Consent (marketing)</span><strong>{{ consentLabel(selectedSession, 'marketing') }}</strong></div>
            <div class="spec-row"><span>DNT</span><strong>{{ flagLabel(selectedSession, ['dnt', 'do_not_track']) }}</strong></div>
            <div class="spec-row"><span>GPC</span><strong>{{ flagLabel(selectedSession, ['gpc', 'global_privacy_control']) }}</strong></div>
          </div>
        </div>

        <div class="panel-section">
          <span class="section-kicker">Event timeline</span>
          <div class="timeline">
            <div v-for="(ev, idx) in selectedEvents" :key="eventId(ev) || idx" class="event-row">
              <span class="event-type" :class="'ev-' + eventType(ev)">{{ prettyEvent(eventType(ev)) }}</span>
              <div class="event-body">
                <span class="event-path" :title="eventPath(ev)">{{ eventPath(ev) || '—' }}</span>
                <span v-if="eventDetail(ev)" class="event-detail">{{ eventDetail(ev) }}</span>
              </div>
              <span class="event-time" :title="fmtTime(eventTime(ev))">{{ fmtTime(eventTime(ev)) }}</span>
            </div>
            <div v-if="!selectedLoading && !selectedEvents.length" class="empty drawer-empty">No events recorded for this session.</div>
          </div>
        </div>
      </aside>
    </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import realtime from '../../services/realtime'
import SearchConsole from './SearchConsole.vue'

export default {
  name: 'Audience',
  components: { SearchConsole },
  data () {
    return {
      activeTab: 'sessions',
      summary: null,
      live: null,
      sessions: [],
      total: 0,
      limit: 25,
      offset: 0,
      filters: { q: '', device: 'all', authenticated: 'all', traffic: 'human' },
      loading: false,
      error: null,
      selectedSession: null,
      selectedEvents: [],
      selectedLoading: false,
      selectedError: '',
      expandedGroups: {},
      crawlerExpanded: false,
      searchTimer: null,
      unsubscribeAudienceLive: null,
      unsubscribeLiveState: null,
      liveFallbackTimer: null,
      liveConnected: false
    }
  },
  computed: {
    summaryCards () {
      const s = this.summary || {}
      const totalSessions = this.numOr(s, ['sessions', 'totalSessions', 'sessionCount', 'total'], 0)
      const totalPageViews = this.numOr(s, ['pageViews', 'page_views', 'pageviews', 'views'], 0)
      const uniqueVisitors = this.numOr(s, ['unique_visitors', 'uniqueVisitors', 'unique_visitor_count', 'uniqueVisitorCount', 'visitors', 'visitorCount'], null)
      return [
        {
          key: 'sessions',
          label: 'Sessions',
          value: totalSessions || '—',
          note: `${uniqueVisitors !== null ? uniqueVisitors : this.visibleUniqueVisitors} unique visitors`,
          progress: this.percent(this.humanSessions.length, totalSessions || this.humanSessions.length)
        },
        {
          key: 'visitors',
          label: 'Unique visitors',
          value: uniqueVisitors !== null ? uniqueVisitors : (this.visibleUniqueVisitors || '—'),
          note: `Human traffic · last ${this.summaryWindowLabel}`,
          progress: this.percent(this.returningVisitorCount, uniqueVisitors || this.visibleUniqueVisitors)
        },
        {
          key: 'pageviews',
          label: 'Page views',
          value: totalPageViews || '—',
          note: `Across ${totalSessions} human sessions`,
          progress: this.percent(this.visiblePageViews, totalPageViews || this.visiblePageViews)
        },
        {
          key: 'avg',
          label: 'Avg. duration',
          value: this.avgDurationLabel(s),
          note: `Human sessions · last ${this.summaryWindowLabel}`,
          progress: this.percent(Math.min(this.visibleAvgDuration, 1800), 1800)
        },
        {
          key: 'engagement',
          label: 'Engagement rate',
          value: `${this.engagementRate}%`,
          note: `${this.engagedSessionCount} engaged session${this.engagedSessionCount === 1 ? '' : 's'}`,
          progress: this.engagementRate
        }
      ]
    },
    humanSessions () {
      return this.sessions.filter((session) => this.trafficType(session) === 'human')
    },
    visiblePageViews () {
      return this.humanSessions.reduce((sum, session) => sum + Number(this.pageViews(session) || 0), 0)
    },
    visibleDuration () {
      return this.humanSessions.reduce((sum, session) => sum + Number(this.durationSeconds(session) || 0), 0)
    },
    visibleAvgDuration () {
      return this.humanSessions.length ? Math.round(this.visibleDuration / this.humanSessions.length) : 0
    },
    visibleUniqueVisitors () {
      const visitors = new Set()
      this.humanSessions.forEach((session, index) => visitors.add(this.audienceGroupKey(session, index)))
      return visitors.size
    },
    visibleAuthSessions () {
      return this.humanSessions.filter((session) => !!this.userLabel(session)).length
    },
    visibleAuthRate () {
      if (!this.humanSessions.length) return 0
      return Math.round((this.visibleAuthSessions / this.humanSessions.length) * 100)
    },
    returningVisitorCount () {
      const counts = {}
      this.humanSessions.forEach((session, index) => {
        const key = this.audienceGroupKey(session, index)
        counts[key] = (counts[key] || 0) + 1
      })
      return Object.values(counts).filter((count) => count > 1).length
    },
    audienceGroups () {
      const groups = {}
      this.sessions.forEach((session, index) => {
        const key = this.audienceGroupKey(session, index)
        if (!groups[key]) {
          const isUser = !!this.userLabel(session)
          groups[key] = {
            key,
            isUser,
            label: isUser ? this.userLabel(session) : 'Anonymous visitor',
            sessions: [],
            visitorIds: [],
            totalPages: 0,
            totalEvents: 0,
            totalDuration: 0,
            amazonClicks: 0,
            live: false
          }
        }
        const group = groups[key]
        group.sessions.push(session)
        const visitor = this.visitorId(session)
        if (visitor && group.visitorIds.indexOf(visitor) === -1) group.visitorIds.push(visitor)
        group.totalPages += Number(this.pageViews(session) || 0)
        group.totalEvents += Number(this.eventCount(session) || 0)
        group.totalDuration += Number(this.durationSeconds(session) || 0)
        group.amazonClicks += Number(this.amazonClicks(session) || 0)
        if (this.isLive(session)) group.live = true
      })

      return Object.values(groups)
        .map((group) => {
          group.sessions = group.sessions.slice().sort((a, b) => {
            return this.timeValue(this.lastSeenAt(b)) - this.timeValue(this.lastSeenAt(a))
          })
          group.lastSession = group.sessions[0] || {}
          group.sessionCount = group.sessions.length
          group.identity = this.audienceGroupIdentity(group)
          group.network = `${this.ipLabel(group.lastSession)} / ${this.timezoneLabel(group.lastSession)}`
          group.lastEventLabel = this.lastEventLabel(group.lastSession)
          group.lastEventTitle = this.lastEventTitle(group.lastSession)
          return group
        })
        .sort((a, b) => {
          return this.timeValue(this.lastSeenAt(b.lastSession)) - this.timeValue(this.lastSeenAt(a.lastSession))
        })
    },
    sourceChart () {
      const colors = ['#1fb2e8', '#1bc98e', '#e0c07e', '#a78bfa', '#f0788c']
      const rows = this.sourcePerformance.slice()
      const top = rows.slice(0, 4)
      if (rows.length > 4) {
        top.push(rows.slice(4).reduce((other, row) => {
          other.sessions += row.sessions
          return other
        }, { label: 'Other', sessions: 0 }))
      }
      const total = top.reduce((sum, row) => sum + row.sessions, 0) || 1
      let allocated = 0
      return top.map((row, index) => {
        const percent = index === top.length - 1
          ? Math.round((100 - allocated) * 10) / 10
          : Math.round((row.sessions / total) * 1000) / 10
        allocated += percent
        return {
          label: row.label,
          count: row.sessions,
          percent,
          color: colors[index % colors.length]
        }
      })
    },
    sourcePerformance () {
      const reported = this.asList(this.summary, ['source_performance', 'sourcePerformance'])
      if (reported.length) {
        return reported.map((row) => ({
          label: this.sourceDisplay(this.pick(row, ['source', 'label', 'value'], 'Direct')),
          sessions: Number(this.pick(row, ['sessions', 'count'], 0)) || 0,
          engaged: Number(this.pick(row, ['engaged_sessions', 'engagedSessions', 'engaged'], 0)) || 0,
          engagementRate: Number(this.pick(row, ['engagement_rate', 'engagementRate'], 0)) || 0,
          pages: Number(this.pick(row, ['page_views', 'pageViews', 'pages'], 0)) || 0,
          duration: Number(this.pick(row, ['duration_seconds', 'durationSeconds', 'duration'], 0)) || 0
        }))
      }

      const grouped = {}
      this.humanSessions.forEach((session) => {
        const label = this.sourceLabel(session)
        if (!grouped[label]) grouped[label] = { label, sessions: 0, engaged: 0, pages: 0, duration: 0 }
        const row = grouped[label]
        row.sessions++
        if (this.sessionQuality(session) === 'engaged') row.engaged++
        row.pages += Number(this.pageViews(session)) || 0
        row.duration += Number(this.durationSeconds(session)) || 0
      })
      return Object.values(grouped).map((row) => Object.assign(row, {
        engagementRate: row.sessions ? Math.round((row.engaged / row.sessions) * 100) : 0
      })).sort((a, b) => b.sessions - a.sessions || b.engaged - a.engaged || a.label.localeCompare(b.label))
    },
    qualityChart () {
      const config = {
        engaged: { label: 'Engaged', note: '2+ pages or 30s+', color: '#1bc98e' },
        browsing: { label: 'Browsing', note: 'A page or 10s+', color: '#1fb2e8' },
        brief: { label: 'Brief / unconfirmed', note: 'Some activity, under 10s', color: '#e0c07e' },
        empty: { label: 'Empty', note: 'No measured activity', color: '#596273' }
      }
      const counts = { engaged: 0, browsing: 0, brief: 0, empty: 0 }
      const reported = this.asList(this.summary, ['engagement_quality', 'engagementQuality'])
      if (reported.length) {
        reported.forEach((row) => {
          const key = String(this.pick(row, ['quality', 'key', 'label'], '')).toLowerCase()
          if (Object.prototype.hasOwnProperty.call(counts, key)) {
            counts[key] = Number(this.pick(row, ['sessions', 'count'], 0)) || 0
          }
        })
      } else {
        this.humanSessions.forEach((session) => { counts[this.sessionQuality(session)]++ })
      }
      const total = Object.values(counts).reduce((sum, count) => sum + count, 0) || 1
      return Object.keys(counts).map((key) => Object.assign({
        key,
        count: counts[key],
        percent: Math.round((counts[key] / total) * 100)
      }, config[key]))
    },
    nonEmptyQualityChart () {
      return this.qualityChart.filter((item) => item.count > 0)
    },
    overviewSessionCount () {
      const reported = this.numOr(this.summary, ['sessions', 'sessionCount'], null)
      return reported !== null ? reported : this.humanSessions.length
    },
    summaryWindowHours () {
      return this.numOr(this.summary, ['windowHours', 'window_hours'], 24) || 24
    },
    summaryWindowLabel () {
      const hours = this.summaryWindowHours
      if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`
      return `${hours} hours`
    },
    engagedSessionCount () {
      const reported = this.numOr(this.summary, ['engaged_sessions', 'engagedSessions'], null)
      if (reported !== null) return reported
      const engaged = this.qualityChart.find((item) => item.key === 'engaged')
      return engaged ? engaged.count : 0
    },
    engagementRate () {
      const reported = this.numOr(this.summary, ['engagement_rate', 'engagementRate'], null)
      return reported !== null ? reported : this.percent(this.engagedSessionCount, this.overviewSessionCount)
    },
    sourceDonutStyle () {
      let cursor = 0
      const total = this.sourceChart.reduce((sum, item) => sum + item.count, 0) || 1
      const stops = this.sourceChart.map((item) => {
        const start = cursor
        cursor += (item.count / total) * 100
        return `${item.color} ${start.toFixed(2)}% ${cursor.toFixed(2)}%`
      })
      return { background: `conic-gradient(${stops.join(', ')})` }
    },
    sourceDonutLabel () {
      return this.sourceChart.map((item) => `${item.label} ${item.percent}%`).join(', ')
    },
    qualityChartLabel () {
      return this.qualityChart.map((item) => `${item.label} ${item.count}`).join(', ')
    },
    crawlerTrafficShare () {
      const crawlers = this.crawlerSessions(this.primaryCrawlerActivity)
      return this.percent(crawlers, this.overviewSessionCount + crawlers)
    },
    sessionListTitle () {
      const labels = {
        all: 'All classified sessions',
        human: 'Human sessions',
        crawler: 'Crawler sessions',
        verified_bot: 'Verified crawler sessions',
        suspected_bot: 'Suspected crawler sessions'
      }
      return labels[this.filters.traffic] || 'Filtered sessions'
    },
    summaryCrawlerActivity () {
      return this.pick(this.summary, ['crawler_activity', 'crawlerActivity'], null)
    },
    liveCrawlerActivity () {
      return this.pick(this.live, ['crawler_activity', 'crawlerActivity'], null)
    },
    crawlerActivityViews () {
      const views = []
      if (this.hasCrawlerActivity(this.summaryCrawlerActivity)) {
        views.push({ key: 'summary', label: 'Summary window', activity: this.summaryCrawlerActivity, live: false })
      }
      if (this.hasCrawlerActivity(this.liveCrawlerActivity)) {
        views.push({ key: 'live', label: 'Live activity', activity: this.liveCrawlerActivity, live: true })
      }
      return views
    },
    primaryCrawlerActivity () {
      return this.summaryCrawlerActivity || this.liveCrawlerActivity || {}
    },
    hasActiveFilters () {
      const f = this.filters
      return !!(f.q || f.device !== 'all' || f.authenticated !== 'all' || f.traffic !== 'human')
    },
    showingFrom () { return this.total === 0 ? 0 : this.offset + 1 },
    showingTo () { return Math.min(this.offset + this.limit, this.total) },
    hasPrev () { return this.offset > 0 },
    hasNext () { return this.offset + this.limit < this.total }
  },
  mounted () {
    this.fetchAll()
    this.connectAudienceLive()
  },
  beforeDestroy () {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.disconnectAudienceLive()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    buildQuery () {
      const f = this.filters
      const p = new URLSearchParams()
      if (f.q) p.set('q', f.q.trim())
      p.set('device', f.device || 'all')
      p.set('authenticated', f.authenticated || 'all')
      p.set('traffic', f.traffic || 'all')
      p.set('dateFrom', new Date(Date.now() - this.summaryWindowHours * 60 * 60 * 1000).toISOString())
      p.set('limit', String(this.limit))
      p.set('offset', String(this.offset))
      return p.toString()
    },
    async fetchAll () {
      await Promise.all([this.fetchSummary(), this.fetchLive(), this.fetchSessions()])
    },
    refreshAudience () {
      this.fetchAll()
      realtime.refresh('audience:live')
    },
    connectAudienceLive () {
      this.liveConnected = realtime.isConnected()
      this.unsubscribeLiveState = realtime.onStateChange((state, connected) => {
        this.liveConnected = !!connected
        if (connected && state === 'open') realtime.refresh('audience:live')
      })
      this.unsubscribeAudienceLive = realtime.subscribe('audience:live', {
        'audience:snapshot': this.handleAudienceLive,
        'audience:update': this.handleAudienceLive
      })
      this.liveFallbackTimer = setTimeout(() => {
        if (!realtime.isConnected()) this.fetchAll()
      }, 5000)
    },
    disconnectAudienceLive () {
      if (this.unsubscribeAudienceLive) this.unsubscribeAudienceLive()
      if (this.unsubscribeLiveState) this.unsubscribeLiveState()
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.unsubscribeAudienceLive = null
      this.unsubscribeLiveState = null
      this.liveFallbackTimer = null
    },
    handleAudienceLive (payload) {
      const data = payload || {}
      const live = this.unwrap(data, ['live', 'data']) || data
      this.live = live
      const summary = this.pick(live, ['summary'], null)
      if (summary) this.summary = summary
      const sessions = this.asList(live, ['sessions', 'items', 'visitors'])
      if (sessions.length && !this.searchTimer) this.mergeSessions(sessions)
      const selected = this.pick(live, ['session', 'selectedSession'], null)
      if (selected) this.mergeSelectedSession(selected)
    },
    mergeSessions (incoming) {
      const byId = {}
      ;(this.sessions || []).forEach((session) => {
        const id = this.sessionId(session)
        if (id) byId[id] = session
      })
      ;(incoming || []).forEach((session) => {
        const id = this.sessionId(session)
        if (!id) return
        byId[id] = Object.assign({}, byId[id] || {}, session)
      })
      this.sessions = Object.keys(byId).map((id) => byId[id]).sort((a, b) => {
        return this.timeValue(this.lastSeenAt(b)) - this.timeValue(this.lastSeenAt(a))
      }).slice(0, this.limit)
      this.total = Math.max(Number(this.total) || 0, this.sessions.length)
    },
    mergeSelectedSession (session) {
      if (!this.selectedSession) return
      const id = this.sessionId(session)
      if (String(id) !== String(this.sessionId(this.selectedSession))) return
      this.selectedSession = Object.assign({}, this.selectedSession, session)
      const events = this.asList(session, ['events'])
      if (events.length) this.selectedEvents = events
    },
    async fetchSummary () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/summary`, { headers: this.authHeaders() })
        const payload = this.unwrap(data, ['data']) || data
        const summary = this.unwrap(payload, ['summary']) || payload
        const crawlerActivity = this.pick(payload, ['crawler_activity', 'crawlerActivity'], null) ||
          this.pick(summary, ['crawler_activity', 'crawlerActivity'], null)
        this.summary = crawlerActivity
          ? Object.assign({}, summary, { crawler_activity: crawlerActivity })
          : summary
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    async fetchLive () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/live`, { headers: this.authHeaders() })
        const payload = this.unwrap(data, ['data']) || data
        this.live = this.unwrap(payload, ['live']) || payload
      } catch (e) {
        // Live crawler activity is supplemental; keep summary and session data usable.
        this.live = null
      }
    },
    async fetchSessions (silent) {
      if (!silent) this.loading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/sessions?${this.buildQuery()}`, { headers: this.authHeaders() })
        this.sessions = this.asList(data, ['sessions', 'items', 'data', 'results'])
        const total = this.pick(data, ['total', 'count'], null)
        this.total = total !== null ? Number(total) : this.sessions.length
        this.error = null
      } catch (e) {
        if (!silent) this.error = this.errMsg(e)
      } finally {
        if (!silent) this.loading = false
      }
    },
    onSearchInput () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.applyFilters(), 400)
    },
    applyFilters () {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null }
      this.offset = 0
      this.fetchSessions()
    },
    clearFilters () {
      this.filters = { q: '', device: 'all', authenticated: 'all', traffic: 'human' }
      this.applyFilters()
    },
    prevPage () {
      if (!this.hasPrev) return
      this.offset = Math.max(0, this.offset - this.limit)
      this.fetchSessions()
    },
    nextPage () {
      if (!this.hasNext) return
      this.offset += this.limit
      this.fetchSessions()
    },
    openDetail (row) {
      this.selectedSession = row
      this.selectedEvents = []
      this.selectedError = ''
      this.fetchDetail(this.sessionId(row))
    },
    closeDetail () {
      this.selectedSession = null
      this.selectedEvents = []
      this.selectedError = ''
    },
    toggleAudienceGroup (key) {
      if (!key) return
      if (this.expandedGroups[key]) {
        this.$delete(this.expandedGroups, key)
      } else {
        this.$set(this.expandedGroups, key, true)
      }
    },
    isGroupExpanded (key) {
      return !!this.expandedGroups[key]
    },
    async fetchDetail (id, silent) {
      if (!id) return
      if (!silent) this.selectedLoading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/audience/sessions/${encodeURIComponent(id)}`, { headers: this.authHeaders() })
        const session = this.unwrap(data, ['session', 'data']) || data
        if (this.selectedSession && String(this.sessionId(this.selectedSession)) === String(id)) {
          this.selectedSession = Object.assign({}, this.selectedSession, session)
          const topEvents = this.asList(data, ['events'])
          this.selectedEvents = topEvents.length ? topEvents : this.asList(session, ['events'])
        }
      } catch (e) {
        if (!silent) this.selectedError = this.errMsg(e)
      } finally {
        if (!silent) this.selectedLoading = false
      }
    },
    // ---- accessors (tolerant to backend field naming) ----
    sessionId (s) { return this.pick(s, ['id', 'session_id', 'sessionId', '_id'], '') },
    visitorId (s) { return this.pick(s, ['visitor_id', 'visitorId', 'visitor'], '') },
    trafficType (s) {
      const type = String(this.pick(s, ['traffic_type', 'trafficType'], 'human') || 'human').toLowerCase()
      return ['human', 'verified_bot', 'suspected_bot'].indexOf(type) >= 0 ? type : 'human'
    },
    isCrawlerSession (s) {
      const type = this.trafficType(s)
      return type === 'verified_bot' || type === 'suspected_bot'
    },
    botProvider (s) {
      return String(this.pick(s, ['bot_provider', 'botProvider'], '') || '').trim()
    },
    botName (s) {
      return String(this.pick(s, ['bot_name', 'botName'], '') || '').trim()
    },
    botVerificationMethod (s) {
      return String(this.pick(s, ['bot_verification_method', 'botVerificationMethod'], '') || '').trim() || '—'
    },
    botVerified (s) {
      return this.truthy(this.pick(s, ['bot_verified', 'botVerified'], false))
    },
    botVerifiedLabel (s) {
      return this.botVerified(s) ? 'Verified' : 'Not verified'
    },
    crawlerBadge (s) {
      if (!this.isCrawlerSession(s)) return ''
      const name = this.botName(s)
      if (this.trafficType(s) === 'verified_bot') {
        const provider = this.botProvider(s)
        const label = provider ? `Verified ${provider}` : 'Verified crawler'
        return name ? `${label} — ${name}` : label
      }
      return name ? `Suspected crawler — ${name}` : 'Suspected crawler'
    },
    audienceGroupKey (session, index) {
      const traffic = this.trafficType(session)
      const user = String(this.userLabel(session) || '').trim().toLowerCase()
      if (user) return `${traffic}:user:${user}`
      const visitor = String(this.visitorId(session) || '').trim()
      if (visitor) return `${traffic}:visitor:${visitor}`
      return `${traffic}:session:${this.sessionId(session) || index}`
    },
    audienceGroupIdentity (group) {
      if (!group) return '-'
      if (group.isUser) {
        const count = group.visitorIds.length
        return count > 1 ? `${count} visitor IDs` : `Visitor ${this.shortId(group.visitorIds[0])}`
      }
      return `Visitor ${this.shortId(group.visitorIds[0])}`
    },
    ipLabel (s) {
      return this.pick(s, ['ip', 'ip_address', 'ipAddress', 'client_ip', 'clientIp', 'remote_ip', 'remoteIp'], '-')
    },
    timezoneLabel (s) {
      return this.pick(s, ['timezone', 'time_zone', 'timeZone', 'tz', 'iana_timezone', 'ianaTimezone'], '-')
    },
    userLabel (s) {
      return this.pick(s, ['user_email', 'email', 'username', 'user_name'], '') ||
        (this.pick(s, ['user_id', 'userId'], null) ? 'User #' + this.pick(s, ['user_id', 'userId'], '') : '')
    },
    startedAt (s) { return this.pick(s, ['started_at', 'startedAt', 'created_at', 'createdAt'], null) },
    endedAt (s) { return this.pick(s, ['ended_at', 'endedAt'], null) },
    lastSeenAt (s) {
      return this.pick(s, ['last_seen_at', 'lastSeenAt', 'ended_at', 'endedAt', 'updated_at', 'updatedAt', 'last_event_at', 'lastEventAt'], null) || this.startedAt(s)
    },
    durationSeconds (s) {
      const direct = this.pick(s, ['duration_seconds', 'durationSeconds', 'duration'], null)
      if (direct !== null) return Number(direct) || 0
      const start = this.startedAt(s)
      const end = this.endedAt(s)
      if (start && end) {
        const startDate = this.parseApiDate(start)
        const endDate = this.parseApiDate(end)
        const ms = startDate && endDate ? endDate.getTime() - startDate.getTime() : 0
        return ms > 0 ? Math.round(ms / 1000) : 0
      }
      return 0
    },
    isLive (s) {
      const end = this.endedAt(s)
      if (end) return false

      const explicitLive = this.pick(s, ['is_live', 'isLive', 'live', 'active'], null)
      const status = String(this.pick(s, ['status', 'state'], '') || '').toLowerCase()
      const lastSeen = this.lastSeenAt(s)
      const lastSeenDate = this.parseApiDate(lastSeen)
      const lastSeenMs = lastSeenDate ? lastSeenDate.getTime() : NaN
      const recentlySeen = Number.isFinite(lastSeenMs) && (Date.now() - lastSeenMs) < 120000

      if (explicitLive !== null) return this.truthy(explicitLive) && recentlySeen
      if (['ended', 'closed', 'complete', 'completed', 'inactive'].indexOf(status) >= 0) return false
      if (['live', 'active', 'open'].indexOf(status) >= 0) return recentlySeen

      return recentlySeen
    },
    pageViews (s) { return this.numOr(s, ['page_views', 'pageViews', 'page_view_count', 'views'], 0) },
    eventCount (s) { return this.numOr(s, ['event_count', 'eventCount', 'events_count', 'events'], 0) },
    deviceType (s) {
      const d = String(this.pick(s, ['device_type', 'deviceType', 'device'], '')).toLowerCase()
      if (d.indexOf('mobile') >= 0 || d.indexOf('phone') >= 0) return 'mobile'
      if (d.indexOf('tablet') >= 0) return 'tablet'
      if (d.indexOf('desktop') >= 0 || d.indexOf('computer') >= 0) return 'desktop'
      return 'other'
    },
    deviceTypeLabel (s) {
      const map = { mobile: 'Mobile', tablet: 'Tablet', desktop: 'Desktop', other: 'Other' }
      return map[this.deviceType(s)]
    },
    browserOs (s) {
      const browser = this.pick(s, ['browser'], '')
      const os = this.pick(s, ['os', 'operating_system'], '')
      return [browser, os].filter(Boolean).join(' · ')
    },
    sourceLabel (s) {
      const utm = String(this.pick(s, ['utm_source', 'utmSource'], '') || '').trim()
      if (utm) {
        if (utm.toLowerCase() === 'chatgpt.com') return 'ChatGPT Search'
        const medium = this.pick(s, ['utm_medium', 'utmMedium'], '')
        return medium ? `${utm} / ${medium}` : utm
      }
      const ref = this.pick(s, ['referrer'], '')
      if (ref) {
        try {
          const hostname = new URL(ref).hostname.toLowerCase().replace(/^www\./, '')
          if (hostname === 'chatgpt.com' || hostname.endsWith('.chatgpt.com')) return 'ChatGPT referral'
          return new URL(ref).hostname
        } catch (e) {
          return ref
        }
      }
      return 'Direct'
    },
    sourceDisplay (value) {
      const source = String(value || '').trim()
      const normalized = source.toLowerCase().replace(/^www\./, '')
      if (!normalized || normalized === 'direct') return 'Direct'
      if (normalized === 'other') return 'Other'
      if (normalized === 'chatgpt.com' || normalized === 'chatgpt') return 'ChatGPT'
      if (normalized === 'google.com' || normalized === 'google') return 'Google'
      return source
    },
    sessionQuality (session) {
      const pages = Number(this.pageViews(session)) || 0
      const events = Number(this.eventCount(session)) || 0
      const duration = Number(this.durationSeconds(session)) || 0
      if (pages >= 2 || duration >= 30) return 'engaged'
      if (pages >= 1 || duration >= 10 || events >= 3) return 'browsing'
      if (events > 0 || duration > 0) return 'brief'
      return 'empty'
    },
    locationLabel (s) {
      const city = this.pick(s, ['city'], '')
      const country = this.pick(s, ['country', 'country_code', 'countryCode'], '')
      const parts = [city, country].filter(Boolean)
      return parts.length ? parts.join(', ') : '—'
    },
    clickIdLabel (s) {
      const g = this.pick(s, ['gclid'], '')
      const f = this.pick(s, ['fbclid'], '')
      const m = this.pick(s, ['msclkid'], '')
      if (g) return 'gclid'
      if (f) return 'fbclid'
      if (m) return 'msclkid'
      return '—'
    },
    consentLabel (s, kind) {
      const consent = this.pick(s, ['consent'], null)
      let v = null
      if (consent && typeof consent === 'object') v = consent[kind]
      if (v === null || v === undefined) {
        v = this.pick(s, ['consent_' + kind, kind === 'analytics' ? 'analytics_consent' : 'marketing_consent'], null)
      }
      if (v === null || v === undefined) return 'Unknown'
      return this.truthy(v) ? 'Granted' : 'Denied'
    },
    flagLabel (s, keys) {
      const v = this.pick(s, keys, null)
      if (v === null) return '—'
      return this.truthy(v) ? 'On' : 'Off'
    },
    avgDurationLabel (s) {
      const v = this.pick(s, ['avgDurationSeconds', 'avg_duration_seconds', 'averageDuration', 'avgDuration'], null)
      if (v === null) return '—'
      return this.fmtDuration(Number(v) || 0)
    },
    durationBucketLabel (seconds) {
      const s = Number(seconds) || 0
      if (s < 60) return 'Under 1m'
      if (s < 300) return '1-5m'
      if (s < 900) return '5-15m'
      return '15m+'
    },
    chartFrom (items, labelFn, limit) {
      const counts = {}
      items.forEach((item) => {
        const label = String(labelFn(item) || 'Unknown')
        counts[label] = (counts[label] || 0) + 1
      })
      const total = items.length || 1
      return Object.keys(counts)
        .map((label) => ({ label, count: counts[label], percent: Math.max(4, Math.round((counts[label] / total) * 100)) }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
        .slice(0, limit || 4)
    },
    topChartLabel (chart) {
      return chart && chart.length ? chart[0].label : '-'
    },
    percent (value, total) {
      const v = Number(value) || 0
      const t = Number(total) || 0
      if (!t) return 0
      return Math.max(0, Math.min(100, Math.round((v / t) * 100)))
    },
    eventId (e) { return this.pick(e, ['id', '_id', 'event_id'], '') },
    eventType (e) {
      const t = String(this.pick(e, ['event_type', 'eventType', 'type'], '')).toLowerCase()
      return t || 'event'
    },
    prettyEvent (t) {
      const map = {
        page_view: 'Page view',
        search: 'Search',
        amazon_click: 'Amazon click',
        amazon_cart_click: 'Amazon cart click',
        button_click: 'Button click',
        product_select: 'Product select',
        product_add: 'Product add',
        product_remove: 'Product remove',
        product_view: 'Product view',
        event: 'Event'
      }
      return map[t] || this.prettyLabel(t)
    },
    // Per-session Amazon-click tally (from the sessions list, if the backend
    // includes it). Drives the table highlight; 0/absent => no badge.
    amazonClicks (s) {
      return this.numOr(s, ['amazon_clicks', 'amazonClicks', 'amazon_click_count', 'amazonClickCount'], 0)
    },
    productClicks (s) {
      return this.numOr(s, ['product_clicks', 'productClicks', 'product_click_count'], 0)
    },
    sessionRowClass (s) {
      return this.amazonClicks(s) > 0 ? 'has-amazon' : ''
    },
    eventPath (e) { return this.pick(e, ['path', 'url'], '') },
    eventTime (e) { return this.pick(e, ['created_at', 'createdAt', 'timestamp', 'time'], null) },
    eventDetail (e) {
      const data = this.pick(e, ['data', 'payload', 'meta'], null)
      if (!data) return ''
      if (typeof data === 'string') return data
      if (typeof data === 'object') {
        if (data.query) return `“${data.query}”`
        if (data.button) return data.button
        if (data.model) return data.asin ? `${data.model} (${data.asin})` : data.model
        if (data.asin) return `ASIN ${data.asin}`
        try { return JSON.stringify(data) } catch (err) { return '' }
      }
      return ''
    },
    lastEventLabel (s) {
      const type = String(this.pick(s, ['last_event_type', 'lastEventType', 'event_type', 'eventType'], '') || '').toLowerCase()
      const path = this.pick(s, ['last_event_path', 'lastEventPath', 'path'], '')
      if (type) return this.prettyEvent(type)
      if (path) return 'Page view'
      return 'No recent event'
    },
    lastEventTitle (s) {
      const label = this.lastEventLabel(s)
      const path = this.pick(s, ['last_event_path', 'lastEventPath', 'path', 'url'], '')
      const detail = this.pick(s, ['last_event_detail', 'lastEventDetail', 'last_event_label', 'lastEventLabel'], '')
      const time = this.lastSeenAt(s)
      return [label, detail, path, time ? this.fmtTime(time) : ''].filter(Boolean).join(' - ')
    },
    timeValue (value) {
      const date = this.parseApiDate(value)
      return date ? date.getTime() : 0
    },
    shortId (id) {
      const s = String(id || '')
      if (!s) return '—'
      return s.length > 10 ? s.slice(0, 8) + '…' : s
    },
    hasCrawlerActivity (activity) {
      return !!activity && typeof activity === 'object' && !Array.isArray(activity)
    },
    crawlerSessions (activity) {
      return this.numOr(activity, ['sessions', 'session_count', 'sessionCount', 'total_sessions', 'totalSessions'], 0)
    },
    crawlerVerifiedCount (activity) {
      return this.numOr(activity, ['verified', 'verified_count', 'verifiedCount', 'verified_sessions', 'verifiedSessions'], 0)
    },
    crawlerSuspectedCount (activity) {
      return this.numOr(activity, ['suspected', 'suspected_count', 'suspectedCount', 'suspected_sessions', 'suspectedSessions'], 0)
    },
    crawlerEvents (activity) {
      return this.numOr(activity, ['events', 'event_count', 'eventCount', 'total_events', 'totalEvents'], 0)
    },
    crawlerLastSeen (activity) {
      return this.pick(activity, ['last_seen', 'lastSeen', 'last_seen_at', 'lastSeenAt'], null)
    },
    crawlerTopCrawlers (activity) {
      return this.asList(activity, ['top_crawlers', 'topCrawlers', 'crawlers', 'bots'])
    },
    crawlerTopPages (activity) {
      return this.asList(activity, ['top_pages', 'topPages', 'pages'])
    },
    crawlerItemKey (item, index) {
      if (typeof item === 'string') return `${item}:${index}`
      return [
        this.pick(item, ['bot_provider', 'botProvider', 'provider'], ''),
        this.pick(item, ['bot_name', 'botName', 'name', 'label', 'crawler', 'bot', 'key', 'value'], ''),
        index
      ].join(':')
    },
    crawlerActivityLabel (item) {
      if (typeof item === 'string') return item
      const provider = String(this.pick(item, ['bot_provider', 'botProvider', 'provider'], '') || '').trim()
      const name = String(this.pick(item, ['bot_name', 'botName', 'name', 'label', 'crawler', 'bot', 'key', 'value'], '') || '').trim()
      if (provider && name && provider.toLowerCase() !== name.toLowerCase()) return `${provider} — ${name}`
      return name || provider || 'Crawler'
    },
    crawlerItemCounts (item) {
      if (!item || typeof item !== 'object') return '—'
      return this.activityCounts(item)
    },
    crawlerPageKey (page, index) {
      return `${this.crawlerPageLabel(page)}:${index}`
    },
    crawlerPageLabel (page) {
      if (typeof page === 'string') return page
      return String(this.pick(page, ['path', 'page', 'page_path', 'pagePath', 'route', 'url', 'label', 'name', 'key', 'value'], 'Unknown page'))
    },
    crawlerPageCounts (page) {
      if (!page || typeof page !== 'object') return '—'
      return this.activityCounts(page)
    },
    activityCounts (item) {
      const sessions = this.numOr(item, ['sessions', 'session_count', 'sessionCount'], null)
      const events = this.numOr(item, ['events', 'event_count', 'eventCount'], null)
      const views = this.numOr(item, ['views', 'page_views', 'pageViews', 'count'], null)
      const counts = []
      if (sessions !== null) counts.push(`${sessions} session${sessions === 1 ? '' : 's'}`)
      if (events !== null) counts.push(`${events} event${events === 1 ? '' : 's'}`)
      if (views !== null && sessions === null && events === null) counts.push(`${views} view${views === 1 ? '' : 's'}`)
      return counts.join(' · ') || '—'
    },
    // ---- generic helpers ----
    pick (obj, keys, fallback) {
      if (!obj) return fallback
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k]
      }
      return fallback
    },
    unwrap (data, keys) {
      for (const k of keys) {
        if (data && data[k] !== undefined && data[k] !== null) return data[k]
      }
      return null
    },
    asList (data, keys) {
      if (Array.isArray(data)) return data
      const v = this.unwrap(data, keys)
      return Array.isArray(v) ? v : []
    },
    truthy (v) {
      if (v === true) return true
      if (typeof v === 'number') return v !== 0
      if (typeof v === 'string') return ['1', 'true', 'yes', 'y', 'on', 'granted'].indexOf(v.toLowerCase()) >= 0
      return false
    },
    numOr (obj, keys, fallback = '—') {
      const v = this.pick(obj, keys, null)
      if (v === null) return fallback
      const n = Number(v)
      return isNaN(n) ? fallback : n
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[._-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    fmtDuration (seconds) {
      const s = Number(seconds) || 0
      if (s <= 0) return '0s'
      if (s < 60) return `${s}s`
      const m = Math.floor(s / 60)
      const rem = s % 60
      if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`
      const h = Math.floor(m / 60)
      return `${h}h ${m % 60}m`
    },
    fmtDate (t) {
      if (!t) return '—'
      const d = this.parseApiDate(t)
      return d ? d.toLocaleDateString() : '—'
    },
    fmtDateTimeShort (t) {
      if (!t) return '—'
      const d = this.parseApiDate(t)
      if (!d) return '—'
      return d.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    fmtTime (t) {
      if (!t) return '—'
      const d = this.parseApiDate(t)
      return d ? d.toLocaleString() : '—'
    },
    fmtRelative (t) {
      if (!t) return '—'
      const d = this.parseApiDate(t)
      if (!d) return '—'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      // A few seconds of clock skew is normal. A larger future value should be
      // explicit instead of silently degrading to a date-only label.
      if (s < -60) return 'clock ahead'
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtDate(t)
    },
    parseApiDate (value) {
      if (!value) return null
      if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

      // Adonis/MySQL may serialize UTC DATETIME values without a timezone.
      // Browsers otherwise interpret `YYYY-MM-DD HH:mm:ss` as local time,
      // shifting recent activity by the viewer's UTC offset.
      let normalized = String(value).trim()
      if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?$/.test(normalized)) {
        normalized = normalized.replace(' ', 'T') + 'Z'
      }
      const date = new Date(normalized)
      return Number.isNaN(date.getTime()) ? null : date
    },
    errMsg (e) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Request failed'
    }
  }
}
</script>

<style scoped>
.audience-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}

/* Top-level view switch: Sessions vs Google Search. Matches the tab idiom used
   by CatalogHealth / ContentManagement. */
.tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 18px 0 4px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
}

.tabs {
  display: flex;
  gap: 4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.tab:hover {
  color: #d7dce8;
}

.tab.active {
  color: #1fb2e8;
  border-bottom-color: #1fb2e8;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 760px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.alert {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(232, 79, 106, 0.5);
  border-radius: 8px;
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
  font-size: 13px;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin: 18px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 6px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  color: #eaf0f7;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.kpi-note {
  overflow: hidden;
  color: #8f98aa;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-progress {
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(52, 58, 72, 0.7);
}

.kpi-progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(25, 151, 198, 0.82), rgba(27, 201, 142, 0.78));
}

.traffic-overview {
  margin: 14px 0 18px;
}

.overview-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.overview-heading h5 {
  margin: 0;
  color: #dce1eb;
  font-size: 14px;
}

.overview-window {
  color: #7f899b;
  font-size: 11px;
  white-space: nowrap;
}

.traffic-overview-grid {
  display: grid;
  grid-template-columns: minmax(250px, 0.85fr) minmax(250px, 0.9fr) minmax(410px, 1.45fr);
  gap: 12px;
}

.insight-card {
  min-width: 0;
  padding: 13px 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(25, 151, 198, 0.07), transparent 42%),
    rgba(9, 13, 20, 0.68);
}

.insight-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
}

.insight-head span {
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.insight-head strong {
  overflow: hidden;
  color: #dce1eb;
  font-size: 12px;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-mix {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
}

.source-donut {
  position: relative;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  box-shadow: 0 0 22px rgba(25, 151, 198, 0.08);
}

.source-donut::after {
  position: absolute;
  inset: 17px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 50%;
  background: #0b1018;
  content: '';
}

.source-donut > div {
  position: absolute;
  z-index: 1;
  inset: 0;
  display: grid;
  place-content: center;
  text-align: center;
}

.source-donut strong,
.source-donut span {
  display: block;
}

.source-donut strong {
  color: #eef3f8;
  font-size: 23px;
  line-height: 1;
}

.source-donut span {
  margin-top: 3px;
  color: #7f899b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.source-legend {
  display: grid;
  gap: 8px;
}

.source-legend-row {
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr) auto 30px;
  align-items: center;
  gap: 7px;
  color: #8993a5;
  font-size: 10.5px;
}

.source-legend-row i,
.quality-row > i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.source-legend-row span {
  overflow: hidden;
  color: #cbd1dc;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-legend-row strong {
  color: #e3e8f1;
}

.source-legend-row small {
  text-align: right;
}

.quality-stack {
  display: flex;
  height: 9px;
  margin: 3px 0 14px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(48, 54, 68, 0.78);
}

.quality-stack i {
  min-width: 2px;
  height: 100%;
}

.quality-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.quality-row {
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
}

.quality-row span,
.quality-row strong,
.quality-row small {
  display: block;
  min-width: 0;
}

.quality-row strong {
  color: #cbd1dc;
  font-size: 11px;
}

.quality-row small {
  overflow: hidden;
  margin-top: 2px;
  color: #737d8f;
  font-size: 9.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quality-row b {
  color: #e4e9f1;
  font-size: 12px;
}

.performance-head,
.performance-row {
  display: grid;
  grid-template-columns: minmax(105px, 1fr) 56px 72px 42px 64px;
  align-items: center;
  gap: 8px;
}

.performance-head {
  padding: 0 7px 6px;
  color: #727c8e;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.performance-head span:not(:first-child),
.performance-row > span:not(:first-child) {
  text-align: right;
}

.performance-list {
  display: grid;
  gap: 3px;
}

.performance-row {
  padding: 7px;
  border-radius: 5px;
  background: rgba(20, 25, 35, 0.5);
  color: #c4cbd7;
  font-size: 10.5px;
}

.performance-source {
  position: relative;
  overflow: hidden;
  min-width: 0;
  padding: 3px 5px;
  border-radius: 4px;
  background: rgba(44, 51, 64, 0.52);
}

.performance-source i {
  position: absolute;
  inset: 0 auto 0 0;
  background: rgba(27, 201, 142, 0.13);
}

.performance-source strong {
  position: relative;
  display: block;
  overflow: hidden;
  color: #dbe1ea;
  font-size: 10.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.performance-engaged {
  color: #79dcb8;
}

.performance-engaged small {
  color: #748092;
  font-size: 9px;
}

.performance-note {
  display: block;
  margin-top: 8px;
  color: #707a8c;
  font-size: 9.5px;
}

.crawler-section {
  margin: 12px 0 16px;
  overflow: hidden;
  border: 1px solid rgba(224, 192, 126, 0.32);
  border-radius: 8px;
  background:
    radial-gradient(circle at 5% 0%, rgba(224, 192, 126, 0.08), transparent 34%),
    rgba(11, 14, 21, 0.72);
  transition: border-color 0.18s ease;
}

.crawler-section.expanded {
  border-color: rgba(224, 192, 126, 0.48);
}

.crawler-summary-toggle {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto auto;
  align-items: center;
  width: 100%;
  gap: 24px;
  padding: 13px 15px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.crawler-summary-toggle:hover {
  background: rgba(224, 192, 126, 0.035);
}

.crawler-summary-toggle:focus-visible {
  outline: 2px solid rgba(25, 151, 198, 0.72);
  outline-offset: -2px;
}

.crawler-summary-toggle h5 {
  margin: 0;
  color: #dce1eb;
  font-size: 14px;
}

.crawler-summary-metrics {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 7px;
}

.crawler-summary-metrics > span {
  padding: 4px 8px;
  border: 1px solid rgba(67, 72, 87, 0.68);
  border-radius: 999px;
  background: rgba(10, 14, 21, 0.58);
  color: #8f98aa;
  font-size: 10px;
  white-space: nowrap;
}

.crawler-summary-metrics strong {
  color: #e0c07e;
  font-size: 11px;
}

.crawler-summary-last strong {
  color: #cbd1dc;
}

.crawler-expand-label {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  min-width: 92px;
  color: #39b4df;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.crawler-chevron {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid rgba(25, 151, 198, 0.5);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.08);
  transition: border-color 0.18s ease, background 0.18s ease;
}

.crawler-chevron::before {
  width: 6px;
  height: 6px;
  margin-top: -3px;
  border-right: 2px solid #7bccee;
  border-bottom: 2px solid #7bccee;
  content: '';
  transform: rotate(45deg);
  transition: margin 0.18s ease, transform 0.18s ease;
}

.crawler-summary-toggle:hover .crawler-chevron {
  border-color: rgba(57, 180, 223, 0.82);
  background: rgba(25, 151, 198, 0.15);
}

.crawler-section.expanded .crawler-chevron::before {
  margin-top: 3px;
  transform: rotate(225deg);
}

.crawler-details {
  padding: 12px 15px 15px;
  border-top: 1px solid rgba(67, 72, 87, 0.64);
}

.crawler-section-note {
  display: block;
  margin: 0 0 10px;
  color: #858fa2;
  font-size: 11px;
}

.crawler-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.crawler-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
  gap: 12px;
}

.crawler-card {
  min-width: 0;
  padding: 13px;
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 8px;
  background: rgba(7, 11, 17, 0.72);
}

.crawler-card-head {
  margin-bottom: 11px;
}

.crawler-card-head > div {
  display: grid;
  gap: 2px;
}

.crawler-card-head span {
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.crawler-card-head strong {
  color: #e0c07e;
  font-size: 14px;
}

.crawler-card-head .live-pill {
  margin-left: 0;
}

.crawler-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  margin-bottom: 12px;
}

.crawler-metrics > div {
  min-width: 0;
  padding: 8px;
  border: 1px solid rgba(67, 72, 87, 0.58);
  border-radius: 6px;
  background: rgba(21, 25, 35, 0.52);
}

.crawler-metrics span,
.crawler-metrics strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crawler-metrics span {
  margin-bottom: 3px;
  color: #7f899b;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.crawler-metrics strong {
  color: #dce1eb;
  font-size: 12px;
}

.crawler-rankings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.crawler-list-title {
  display: block;
  margin-bottom: 6px;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.crawler-list {
  display: grid;
  gap: 5px;
}

.crawler-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: 10px;
  color: #8690a2;
  font-size: 10.5px;
}

.crawler-list-row strong {
  overflow: hidden;
  color: #cbd1dc;
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crawler-list-row span {
  flex: 0 0 auto;
  white-space: nowrap;
}

.crawler-list-empty {
  color: #70798b;
  font-size: 10.5px;
}

.session-list-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
  margin: 18px 0 8px;
}

.session-list-heading h5 {
  margin: 0;
  color: #dce1eb;
  font-size: 14px;
}

.session-list-heading > span {
  color: #7f899b;
  font-size: 11px;
}

.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 12px;
}

.hfilter {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #cfd2da;
  padding: 0 9px;
  font-size: 12px;
  outline: none;
}

.hfilter.grow {
  flex: 1;
  min-width: 240px;
}

.hfilter:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

select.hfilter {
  color-scheme: dark;
}

select.hfilter option {
  background: #0d121b;
  color: #cfd2da;
}

select.hfilter option:checked {
  background: #15384b;
  color: #e8f6fc;
}

.focus-clear {
  color: #1fb2e8;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
}

.audience-groups {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(8, 12, 18, 0.48);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
}

.audience-groups.is-loading {
  border-color: rgba(25, 151, 198, 0.45);
}

.group-head,
.group-row {
  display: grid;
  grid-template-columns: minmax(220px, 1.35fr) 110px minmax(130px, 0.8fr) minmax(130px, 0.8fr) 105px 135px 120px 52px;
  gap: 12px;
  align-items: center;
}

.group-head {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  color: #818b9d;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.visitor-group {
  border-bottom: 1px solid rgba(47, 51, 61, 0.78);
}

.visitor-group:last-child {
  border-bottom: 0;
}

.visitor-group.expanded {
  background: rgba(25, 151, 198, 0.035);
}

.group-row {
  min-height: 76px;
  padding: 12px;
  cursor: pointer;
  outline: none;
}

.group-row:hover,
.group-row:focus {
  background: rgba(25, 151, 198, 0.06);
}

.person-cell {
  min-width: 0;
}

.person-cell .user-name,
.person-cell .anon-pill {
  margin-bottom: 3px;
}

.network-line {
  display: block;
  overflow: hidden;
  max-width: 100%;
  margin-top: 2px;
  color: #6f7b91;
  font-size: 10.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-cell {
  display: block;
  overflow: hidden;
  max-width: 100%;
  color: #dce1eb;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-toggle {
  justify-self: end;
  min-width: 44px;
  padding: 5px 8px;
  border: 1px solid rgba(25, 151, 198, 0.55);
  border-radius: 999px;
  color: #1fb2e8;
  font-size: 10.5px;
  font-weight: 800;
  text-align: center;
  text-transform: uppercase;
}

.session-dropdown {
  display: grid;
  gap: 6px;
  padding: 0 12px 12px;
}

.session-mini-row {
  display: grid;
  grid-template-columns: minmax(120px, 0.9fr) minmax(100px, 0.7fr) minmax(180px, 1.25fr) minmax(90px, 0.6fr) minmax(130px, 0.8fr);
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(67, 72, 87, 0.66);
  border-radius: 7px;
  background: rgba(10, 14, 22, 0.62);
  color: #cfd4df;
  cursor: pointer;
  text-align: left;
}

.session-mini-row:hover {
  border-color: rgba(25, 151, 198, 0.52);
  background: rgba(25, 151, 198, 0.07);
}

.session-mini-row span {
  min-width: 0;
}

.session-mini-row strong,
.session-mini-row small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-mini-row strong {
  color: #dce1eb;
  font-size: 12px;
}

.session-mini-row small {
  margin-top: 2px;
  color: #7f899b;
  font-size: 10.5px;
}

.audience-empty {
  padding: 28px 16px;
  color: #8f98aa;
  text-align: center;
}

.btn-refresh {
  min-width: 84px;
  height: 31px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.btn-refresh:disabled {
  border-color: rgba(25, 151, 198, 0.42);
  background: rgba(25, 151, 198, 0.07);
  color: #78aabe;
  cursor: wait;
}

.audience-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.live-connection {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 999px;
  padding: 7px 10px;
  color: #aab5c7;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.live-connection i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f5c84c;
  box-shadow: 0 0 8px rgba(245, 200, 76, 0.45);
}

.live-connection.online {
  color: #7ef0c9;
  border-color: rgba(20, 184, 166, 0.32);
}

.live-connection.online i {
  background: #18d59f;
  box-shadow: 0 0 10px rgba(24, 213, 159, 0.55);
}

.mono { font-family: monospace; }

.session-id {
  color: #cfe6f3;
  font-weight: 600;
}

.user-name {
  color: #dce1eb;
  font-weight: 600;
}

.muted-sub {
  display: block;
  color: #7f899b;
  font-size: 11px;
}

.when-primary { color: #cfd2da; }

.count-num {
  color: #eaf0f7;
  font-weight: 700;
}

.anon-pill,
.device-pill,
.live-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.anon-pill {
  background: rgba(58, 63, 77, 0.5);
  color: #9aa3b4;
}

.device-pill.dv-desktop {
  border-color: rgba(25, 151, 198, 0.36);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.device-pill.dv-mobile {
  border-color: rgba(27, 201, 142, 0.4);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.device-pill.dv-tablet {
  border-color: rgba(224, 192, 126, 0.4);
  background: rgba(224, 192, 126, 0.1);
  color: #e0c07e;
}

.device-pill.dv-other {
  background: rgba(58, 63, 77, 0.5);
  color: #aeb5c5;
}

.live-pill {
  margin-left: 6px;
  border-color: rgba(27, 201, 142, 0.5);
  background: rgba(27, 201, 142, 0.14);
  color: #89dfbd;
}

.crawler-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 7px;
  overflow: hidden;
  border: 1px solid rgba(224, 192, 126, 0.4);
  border-radius: 999px;
  background: rgba(224, 192, 126, 0.11);
  color: #e4c985;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.025em;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.crawler-badge--verified_bot {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.11);
  color: #8ce1c0;
}

.person-cell .crawler-badge,
.session-id .crawler-badge {
  margin: 2px 0 3px;
}

.session-id .crawler-badge {
  display: flex;
  width: fit-content;
}

.row-btn {
  height: 27px;
  padding: 0 11px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
}

.row-btn:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.row-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}

.pager-info {
  color: #8f98aa;
  font-size: 12px;
}

.pager-buttons .row-btn {
  margin-left: 8px;
}

/* Drawer */
.audience-drawer {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.52);
  backdrop-filter: blur(2px);
}

.audience-panel {
  width: min(560px, calc(100vw - 28px));
  height: 100%;
  padding: 22px;
  overflow-y: auto;
  border-left: 1px solid rgba(67, 72, 87, 0.9);
  background:
    radial-gradient(circle at 20% 0%, rgba(25, 151, 198, 0.14), transparent 34%),
    rgba(9, 13, 20, 0.96);
  box-shadow: -24px 0 65px rgba(0, 0, 0, 0.42);
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 16px;
}

.panel-head h5 {
  margin: 6px 0 2px;
  color: #eaf0f7;
  font-size: 17px;
  font-weight: 800;
}

.panel-close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 14px;
}

.panel-close:hover {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.panel-loading { color: #8f98aa; font-size: 13px; margin-bottom: 12px; }
.panel-error { color: #f0a1af; font-size: 13px; margin-bottom: 12px; }

.metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 18px;
}

.metrics > div {
  display: grid;
  gap: 4px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.7);
}

.metric-label {
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.metrics strong {
  color: #eaf0f7;
  font-size: 18px;
  font-weight: 800;
}

.panel-section {
  margin-bottom: 18px;
}

.section-kicker {
  display: block;
  margin-bottom: 8px;
  color: #1997c6;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.spec-grid {
  display: grid;
  gap: 1px;
  border: 1px solid rgba(67, 72, 87, 0.74);
  border-radius: 8px;
  overflow: hidden;
}

.spec-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  background: rgba(14, 18, 27, 0.6);
}

.spec-row span {
  color: #8f98aa;
  font-size: 12px;
  flex: 0 0 auto;
}

.spec-row strong {
  color: #dce1eb;
  font-size: 12.5px;
  font-weight: 700;
  text-align: right;
  word-break: break-all;
}

.spec-row strong.ua {
  font-size: 11.5px;
  font-weight: 500;
  color: #aeb5c5;
}

.timeline {
  display: grid;
  gap: 6px;
}

.event-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 7px;
  background: rgba(14, 18, 27, 0.6);
}

.event-type {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  background: rgba(58, 63, 77, 0.5);
  color: #aeb5c5;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  white-space: nowrap;
}

.event-type.ev-page_view {
  border-color: rgba(25, 151, 198, 0.36);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.event-type.ev-search {
  border-color: rgba(224, 192, 126, 0.4);
  background: rgba(224, 192, 126, 0.1);
  color: #e0c07e;
}

.event-type.ev-amazon_click,
.event-type.ev-amazon_cart_click {
  border-color: rgba(27, 201, 142, 0.4);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.event-type.ev-product_select,
.event-type.ev-product_add,
.event-type.ev-product_view {
  border-color: rgba(25, 151, 198, 0.36);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.event-type.ev-product_remove {
  border-color: rgba(232, 79, 106, 0.4);
  background: rgba(232, 79, 106, 0.1);
  color: #f0a1af;
}

.event-type.ev-button_click {
  border-color: rgba(174, 130, 255, 0.4);
  background: rgba(123, 86, 209, 0.16);
  color: #c2acff;
}

/* Sessions cell: count + Live + Amazon badge on one aligned row. */
.sess-line {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

/* Amazon-click highlight: a cart badge on visitor groups / sessions that had
   outbound affiliate clicks, plus a subtle left accent on those groups. Sized to
   match the Live pill (same height/font) so the row stays even. */
.amazon-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border: 1px solid rgba(27, 201, 142, 0.5);
  border-radius: 999px;
  background: rgba(27, 201, 142, 0.14);
  color: #89dfbd;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.04em;
  white-space: nowrap;
  vertical-align: middle;
}

.amazon-badge .icon {
  font-size: 11px;
  line-height: 1;
}

/* In the expanded session rows the badge stacks under the events count. */
.session-mini-row .amazon-badge {
  margin-top: 3px;
}

.visitor-group.has-amazon {
  box-shadow: inset 3px 0 0 rgba(27, 201, 142, 0.7);
}

.event-body {
  min-width: 0;
}

.event-path {
  display: block;
  overflow: hidden;
  color: #cfd4df;
  font-size: 12.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-detail {
  display: block;
  color: #8f98aa;
  font-size: 11px;
}

.event-time {
  color: #7f899b;
  font-size: 11px;
  white-space: nowrap;
}

.empty {
  padding: 18px;
  color: #6f788b;
  text-align: center;
}

.drawer-empty {
  border: 1px dashed rgba(67, 72, 87, 0.7);
  border-radius: 7px;
}

@media screen and (max-width: 1100px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .traffic-overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .source-performance-card {
    grid-column: 1 / -1;
  }

  .crawler-card-grid {
    grid-template-columns: 1fr;
  }

  .crawler-summary-toggle {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px 18px;
  }

  .crawler-summary-metrics {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-content: flex-start;
  }

  .group-head {
    display: none;
  }

  .group-row {
    grid-template-columns: minmax(220px, 1fr) 95px minmax(120px, 0.7fr) 95px 58px;
  }

  .group-row > div:nth-child(3),
  .group-row > div:nth-child(6),
  .group-row > div:nth-child(7) {
    display: none;
  }

  .session-mini-row {
    grid-template-columns: minmax(120px, 0.9fr) minmax(110px, 0.7fr) minmax(160px, 1fr);
  }

  .session-mini-row > span:nth-child(4),
  .session-mini-row > span:nth-child(5) {
    display: none;
  }
}

@media screen and (max-width: 720px) {
  .kpi-row {
    grid-template-columns: 1fr;
  }

  .overview-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .session-list-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .traffic-overview-grid {
    grid-template-columns: 1fr;
  }

  .source-performance-card {
    grid-column: auto;
  }

  .performance-head,
  .performance-row {
    grid-template-columns: minmax(95px, 1fr) 48px 65px 36px 58px;
    gap: 5px;
  }

  .crawler-summary-toggle {
    grid-template-columns: 1fr;
  }

  .crawler-summary-metrics {
    grid-column: 1;
    grid-row: auto;
    justify-content: flex-start;
  }

  .crawler-expand-label {
    justify-content: flex-start;
  }

  .crawler-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .crawler-rankings {
    grid-template-columns: 1fr;
  }

  .group-row,
  .session-mini-row {
    grid-template-columns: 1fr;
  }

  .group-row > div:nth-child(3),
  .group-row > div:nth-child(6),
  .group-row > div:nth-child(7),
  .session-mini-row > span:nth-child(4),
  .session-mini-row > span:nth-child(5) {
    display: block;
  }

  .group-toggle {
    justify-self: start;
  }

  .pager {
    align-items: flex-start;
    flex-direction: column;
  }

  .pager-buttons .row-btn {
    margin-left: 0;
    margin-right: 8px;
  }
}
</style>
